import { useEffect, useMemo, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl';

import type { Map as ImmutableMap } from 'immutable';

import { importFetchedStatuses } from '@/flavours/glitch/actions/importer';
import { apiGetSearch } from '@/flavours/glitch/api/search';
import { supportsQuotePolicy } from '@/flavours/glitch/initial_state';
import type { Status } from '@/flavours/glitch/models/status';
import { useAppDispatch, useAppSelector } from '@/flavours/glitch/store';
import type { RootState } from '@/flavours/glitch/store';

import StatusContainer from '../containers/status_container';
import { makeGetStatusWithExtraInfo } from '../selectors';

const MAX_QUOTE_POSTS_NESTING_LEVEL = 1;

const STATUS_URL_PATTERNS = [
  /^https?:\/\/[^/]+\/@[^/]+\/.+/i,
  /^https?:\/\/[^/]+\/users\/[^/]+\/.+/i,
  /^https?:\/\/[^/]+\/user\/[^/]+\/.+/i,
  /^https?:\/\/[^/]+\/posts\/.+/i,
  /^https?:\/\/[^/]+\/post\/.+/i,
  /^https?:\/\/[^/]+\/objects\/.+/i,
  /^https?:\/\/[^/]+\/object\/.+/i,
  /^https?:\/\/[^/]+\/notes\/.+/i,
  /^https?:\/\/[^/]+\/note\/.+/i,
];

function isLikelyStatusUrl(url: string): boolean {
  return STATUS_URL_PATTERNS.some((pattern) => pattern.test(url));
}

function extractLinksFromHtml(contentHtml: string): string[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(contentHtml, 'text/html');
  const links = Array.from(doc.querySelectorAll('a'));
  return links
    .map((a) => a.href)
    .filter(
      (href) =>
        href &&
        (href.startsWith('http://') || href.startsWith('https://')) &&
        isLikelyStatusUrl(href),
    );
}

type QuoteMap = ImmutableMap<'state' | 'quoted_status', string | null>;

interface StatusWithExtraInfo {
  status: Status | null;
  loadingState: 'not-found' | 'loading' | 'filtered' | 'complete';
}

interface StatusQuoteFallbackProps {
  status: Status;
  contextType?: string;
  nestingLevel?: number;
}

// Standalone: on instances without native quote support, posts that quote
// another post do so by appending its URL (`RE: <url>`). This component detects
// such a trailing status URL and renders the referenced post inline, mimicking
// the native quote UI.
export const StatusQuoteFallback: React.FC<StatusQuoteFallbackProps> = ({
  status,
  contextType,
  nestingLevel = 1,
}) => {
  const dispatch = useAppDispatch();

  const nativeQuote = status.get('quote') as QuoteMap | undefined;

  const [quotedStatusId, setQuotedStatusId] = useState<string | null>(null);
  const [resolveFailed, setResolveFailed] = useState(false);
  const hasAttemptedResolve = useRef(false);

  const contentHtml = status.get('contentHtml') as string | undefined;
  const links = useMemo(
    () => (contentHtml ? extractLinksFromHtml(contentHtml) : []),
    [contentHtml],
  );

  const getStatusSelector = useMemo(
    () =>
      makeGetStatusWithExtraInfo() as unknown as (
        state: RootState,
        props: { id: string | null; contextType?: string },
      ) => StatusWithExtraInfo,
    [],
  );

  const { status: quotedStatus, loadingState } = useAppSelector((state) =>
    getStatusSelector(state, { id: quotedStatusId, contextType }),
  );

  useEffect(() => {
    if (links.length === 0 || hasAttemptedResolve.current) {
      return;
    }

    hasAttemptedResolve.current = true;

    const resolveLinks = async () => {
      for (const link of links) {
        try {
          const result = await apiGetSearch({
            q: link,
            resolve: true,
            type: 'statuses',
            limit: 1,
          });

          const resolvedStatus = result.statuses[0];
          if (resolvedStatus) {
            dispatch(importFetchedStatuses([resolvedStatus]));
            setQuotedStatusId(resolvedStatus.id);
            return;
          }
        } catch (error) {
          console.error('Failed to resolve link:', link, error);
        }
      }

      setResolveFailed(true);
    };

    void resolveLinks();
  }, [links, dispatch]);

  if (supportsQuotePolicy || nativeQuote) {
    return null;
  }

  if (links.length === 0) {
    return null;
  }

  if (!quotedStatusId) {
    return null;
  }

  if (resolveFailed) {
    return null;
  }

  if (!quotedStatus || loadingState === 'not-found') {
    return null;
  }

  if (loadingState === 'filtered') {
    return (
      <div className='status__quote status__quote--error'>
        <FormattedMessage
          id='status.quote_error.filtered'
          defaultMessage='Hidden due to one of your filters'
        />
      </div>
    );
  }

  const childQuote = quotedStatus.get('quote') as QuoteMap | undefined;
  const canRenderChildQuote =
    childQuote && nestingLevel <= MAX_QUOTE_POSTS_NESTING_LEVEL;

  return (
    <div className='status__quote'>
      <StatusContainer
        isQuotedPost
        id={quotedStatusId}
        contextType={contextType}
        avatarSize={32}
      >
        {canRenderChildQuote && (
          <StatusQuoteFallback
            status={quotedStatus}
            contextType={contextType}
            nestingLevel={nestingLevel + 1}
          />
        )}
      </StatusContainer>
    </div>
  );
};
